import type { AiProofreadingResultPayload } from '@/lib/api/proofreadingAi';
import type { ProofreadingMode } from '@/stores/proofreadingAi';
import { collectManuscriptIdsFromAiResult } from '@/lib/utils/proofreadingTransform';

export function normalizeProofreadingRouteMode(
  routeMode: unknown,
): 'ai' | 'basic' | undefined {
  if (routeMode === 'ai') return 'ai';
  if (routeMode === 'basic') return 'basic';
  return undefined;
}

/**
 * 修正反映画面が AI 校正結果のレビューかどうか。
 * ルート `mode=ai` より、当該作品の aiResult 保持を優先する（session 復元・作業中データ再開用）。
 */
export function isAiProofreadingReviseContext(params: {
  novelId: string | undefined;
  routeMode?: unknown;
  aiResult: AiProofreadingResultPayload | null;
}): boolean {
  const { novelId, aiResult } = params;
  if (!novelId || aiResult == null) {
    return false;
  }

  if (aiResult.novelId !== novelId) {
    return false;
  }

  const routeMode = normalizeProofreadingRouteMode(params.routeMode);
  if (routeMode === 'basic') {
    return false;
  }

  return true;
}

/**
 * ウィザード上で AI フローに入っているか（修正反映画面以外）。
 */
/**
 * AI 修正反映画面で使う選択原稿キー。
 * store → aiResult → work 一覧の順で補完する。
 */
function filterByValidManuscriptKeys(
  keys: string[],
  validManuscriptKeys?: ReadonlySet<string>,
): string[] {
  if (validManuscriptKeys == null || validManuscriptKeys.size === 0) {
    return keys;
  }
  return keys.filter((key) => validManuscriptKeys.has(key));
}

/**
 * 執筆画面の並び（displayOrder）に合わせる。
 * aiResult / work 一覧の返却順は Gemini 出力順・S3 列挙順で意味を持たないため、
 * ユーザーの選択順ではないキーはこちらで並べ直す。
 */
function orderByManuscriptKeyOrder(keys: string[], manuscriptKeyOrder?: string[]): string[] {
  if (manuscriptKeyOrder == null || manuscriptKeyOrder.length === 0) {
    return keys;
  }

  const remaining = new Set(keys);
  const ordered: string[] = [];
  manuscriptKeyOrder.forEach((key) => {
    if (remaining.delete(key)) {
      ordered.push(key);
    }
  });
  remaining.forEach((key) => ordered.push(key));

  return ordered;
}

export function resolveAiReviseSelectedManuscriptKeys(params: {
  storeKeys: string[];
  aiResult: AiProofreadingResultPayload | null;
  workManuscriptIds: string[];
  /** 原稿一覧に存在する key のみ残す（孤立 work / 削除済み原稿の除外）。 */
  validManuscriptKeys?: ReadonlySet<string>;
  /** 執筆画面の原稿 key 順。store の選択順以外はこの順に整える。 */
  manuscriptKeyOrder?: string[];
}): string[] {
  const { manuscriptKeyOrder } = params;
  // 絞り込みで空になった候補は採用しない。別作品の選択キーが残っていても
  // aiResult / work 一覧まで辿って当該作品の原稿を必ず解決する。
  const candidates = [
    params.storeKeys,
    orderByManuscriptKeyOrder(
      collectManuscriptIdsFromAiResult(params.aiResult),
      manuscriptKeyOrder,
    ),
    orderByManuscriptKeyOrder(params.workManuscriptIds, manuscriptKeyOrder),
  ].map((keys) => filterByValidManuscriptKeys(keys, params.validManuscriptKeys));

  return candidates.find((keys) => keys.length > 0) ?? [];
}

export function isAiProofreadingWizardContext(params: {
  novelId: string | undefined;
  routeMode?: unknown;
  storeMode: ProofreadingMode;
}): boolean {
  const routeMode = normalizeProofreadingRouteMode(params.routeMode);
  if (routeMode === 'ai') return true;
  if (routeMode === 'basic') return false;
  return params.storeMode === 'ai';
}
